import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import ShopNav from "@/components/ShopNav";
import Footer from "@/components/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OrderSuccessPage() {
  const { t } = useLang();
  const loc = useLocation();
  const sessionId = new URLSearchParams(loc.search).get("session_id");
  const [state, setState] = useState({ status: "checking", data: null, error: null });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "failed", data: null, error: "missing_session_id" });
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        const tx = res.data;
        if (tx.payment_status === "paid") {
          setState({ status: "paid", data: tx, error: null });
          return;
        }
        if (tx.status === "expired" || tx.payment_status === "expired") {
          setState({ status: "failed", data: tx, error: "expired" });
          return;
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "failed", data: tx, error: "timeout" });
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        if (typeof process !== "undefined" && process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("OrderSuccess poll error", err);
        }
        if (attemptsRef.current >= 8) {
          setState({ status: "failed", data: null, error: "fetch" });
        } else {
          setTimeout(poll, 2500);
        }
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // The poll closure intentionally captures `API` and `attemptsRef` which are
    // module-level / ref-stable. We only want to restart polling when the
    // session_id in the URL changes — not on every re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const order = state.data?.order;
  const euro = (n) => (n || 0).toFixed(2).replace(".", ",") + " €";

  return (
    <main data-testid="success-page" className="min-h-screen bg-cream-100 text-charcoal">
      <ShopNav />
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-20 sm:py-28 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="mx-auto text-gold-deep animate-spin" size={48} />
            <h1 className="mt-8 font-serif text-3xl text-olive-900">{t("success_page.processing")}</h1>
          </>
        )}

        {state.status === "paid" && (
          <>
            <img
              src="https://customer-assets.emergentagent.com/job_aceite-granada/artifacts/d8ycwu18_Logo%20CSM.webp"
              alt=""
              width="88"
              height="88"
              style={{ mixBlendMode: "multiply" }}
              className="mx-auto"
              data-testid="success-brand-crest"
            />
            <CheckCircle2 className="mx-auto mt-6 text-olive-700" size={36} strokeWidth={1.4} />
            <span className="mt-6 inline-block text-[11px] uppercase tracking-[0.32em] text-gold-deep">
              {t("success_page.paid")}
            </span>
            <h1 className="mt-4 font-serif text-4xl sm:text-5xl text-olive-900">
              {t("success_page.title")}
            </h1>
            <p className="mt-4 text-olive-800/80 font-light">{t("success_page.subtitle")}</p>
            {order && (
              <dl className="mt-10 inline-grid grid-cols-2 gap-x-10 gap-y-3 text-left bg-cream-200 border border-cream-400 p-6 sm:p-8">
                <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60 self-center">
                  {t("success_page.order_number")}
                </dt>
                <dd data-testid="success-order-number" className="font-serif text-olive-900 text-xl">
                  {order.order_number}
                </dd>
                <dt className="text-[10px] uppercase tracking-[0.28em] text-olive-800/60 self-center">
                  {t("success_page.total")}
                </dt>
                <dd className="font-serif text-olive-900 text-xl">{euro(order.total)}</dd>
              </dl>
            )}
          </>
        )}

        {state.status === "failed" && (
          <>
            <AlertCircle className="mx-auto text-red-700" size={48} />
            <h1 className="mt-6 font-serif text-3xl text-olive-900">{t("success_page.failed")}</h1>
            <p className="mt-4 text-olive-800/80 font-light">{t("common.error_generic")}</p>
          </>
        )}

        <div className="mt-12 flex flex-wrap justify-center gap-4">
          <Link
            to="/"
            className="px-6 py-3 border border-olive-700 text-olive-800 text-[12px] uppercase tracking-[0.26em] hover:bg-cream-200"
          >
            {t("success_page.back_home")}
          </Link>
          <Link
            to="/tienda"
            className="px-6 py-3 bg-olive-700 text-cream-100 text-[12px] uppercase tracking-[0.26em] hover:bg-olive-800"
          >
            {t("success_page.shop_more")}
          </Link>
        </div>
        {state.status === "paid" && (
          <aside
            data-testid="success-withdrawal-info"
            role="note"
            className="mt-12 max-w-xl mx-auto border-t border-cream-400 pt-8 text-center"
          >
            <div className="text-[11px] uppercase tracking-[0.28em] text-olive-800/60 font-medium">
              {t("success_page.withdrawal_title")}
            </div>
            <p className="mt-3 text-sm text-olive-800/85 font-light leading-relaxed">
              {t("success_page.withdrawal_body")}
            </p>
            <Link
              to="/desistimiento"
              data-testid="success-withdrawal-link"
              className="mt-4 inline-block text-[11px] uppercase tracking-[0.24em] text-olive-900 underline decoration-gold underline-offset-4 hover:text-gold-deep"
            >
              {t("success_page.withdrawal_cta")} →
            </Link>
            <p className="mt-3 text-xs text-olive-800/60 font-light italic">
              {t("success_page.withdrawal_note")}
            </p>
          </aside>
        )}
      </section>
      <Footer />
    </main>
  );
}
